import { getOrphanPaymentsCount } from '@/lib/api/financial/orphan-payments-api'
import { useCallback, useEffect, useState } from 'react'

const POLL_INTERVAL_MS = 5 * 60 * 1000

/** Disparado depois de vincular ou dispensar um Pix: o badge recarrega na hora. */
export const ORPHAN_PAYMENTS_CHANGED_EVENT = 'orphan-payments:changed'

export function notifyOrphanPaymentsChanged() {
    window.dispatchEvent(new Event(ORPHAN_PAYMENTS_CHANGED_EVENT))
}

/**
 * Quantos Pix estão sem vínculo — o badge vermelho do rodapé do sidebar.
 *
 * Polling de 5 minutos, mais ao voltar para a aba e quando a tela de pagamentos sem vínculo
 * resolve um. Não há evento de socket para o Pix que chega; 5 minutos bastam para chamar a
 * atenção antes de alguém registrar o mesmo valor à mão. `enabled` falso (sem acesso ao
 * Extrato) não faz requisição nenhuma.
 */
export function useOrphanPaymentsCount(enabled: boolean): number {
    const [count, setCount] = useState(0)

    const load = useCallback(async () => {
        if (!enabled) return
        try {
            const response = await getOrphanPaymentsCount()
            if (response.success) setCount(response.result?.count ?? 0)
        } catch {
            // Badge é aviso, não bloqueio: falha de rede não pode quebrar o sidebar.
        }
    }, [enabled])

    useEffect(() => {
        if (!enabled) {
            setCount(0)
            return
        }
        load()
        const timer = window.setInterval(load, POLL_INTERVAL_MS)
        const onVisible = () => document.visibilityState === 'visible' && load()
        window.addEventListener(ORPHAN_PAYMENTS_CHANGED_EVENT, load)
        window.addEventListener('focus', load)
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            window.clearInterval(timer)
            window.removeEventListener(ORPHAN_PAYMENTS_CHANGED_EVENT, load)
            window.removeEventListener('focus', load)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [enabled, load])

    return count
}
